/**
 * Landing Page
 * 
 * Public-facing page with call-to-action to apply for membership.
 * Content is dynamically loaded from settings (editable by Super Admin).
 * 
 * Image URLs are returned as fully qualified URLs from the backend,
 * ensuring compatibility with reverse proxies and different environments.
 */

import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Shield, Users, FileText, CreditCard, Star, Award, Heart, Zap } from 'lucide-react';
import apiClient from '@/utils/api';
import SiteHeader from '@/components/SiteHeader';
import HeroSection from '@/components/HeroSection';
import { trackCtaClick } from '@/utils/heroStyles';

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
};

// Icon mapping for dynamic icons
const iconMap = {
  Shield,
  Users,
  FileText,
  CreditCard,
  Star,
  Award,
  Heart,
  Zap
};

const Landing = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [branding, setBranding] = useState(null);
  const [hero, setHero] = useState(null);
  const [header, setHeader] = useState(null);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const [landingRes, brandingRes, heroRes] = await Promise.all([
        apiClient.get('/settings/landing-page'),
        apiClient.get('/settings/branding'),
        apiClient.get('/settings/hero')
      ]);
      setContent(landingRes.data);
      setBranding(brandingRes.data);
      setHero(heroRes.data.hero);
      setHeader(heroRes.data.header);
    } catch (error) {
      // Use defaults if API fails
      setContent({
        hero_title: "Professional Membership Management",
        hero_subtitle: "Join our community and enjoy seamless membership management, secure document storage, and transparent payment tracking.",
        hero_image_url: "https://images.pexels.com/photos/7640434/pexels-photo-7640434.jpeg",
        hero_button_text: "Apply for Membership",
        features_title: "Why Choose Us",
        features_subtitle: "We provide everything you need for professional membership management",
        features: [
          { icon: "Shield", title: "Secure Management", description: "Your membership data is protected with enterprise-grade security" },
          { icon: "Users", title: "Easy Registration", description: "Simple application process with step-by-step guidance" },
          { icon: "FileText", title: "Document Portal", description: "Access your invoices, receipts, and statements anytime" },
          { icon: "CreditCard", title: "Flexible Payments", description: "Multiple payment options with secure verification" },
        ],
        cta_title: "Ready to Join?",
        cta_subtitle: "Start your membership application today and become part of our community",
        cta_button_text: "Apply Now"
      });
      setBranding({ app_name: 'MemberHub' });
    } finally {
      setLoading(false);
    }
  };

  // Get icon component from string name
  const getIcon = (iconName) => {
    return iconMap[iconName] || Shield;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const appName = branding?.app_name || 'MemberHub';

  return (
    <div className="min-h-screen bg-background font-sans">
      <SiteHeader branding={branding} header={header} overHero mobile={isMobile} />

      <HeroSection hero={hero} mobile={isMobile} />

      {/* Features Section */}
      <section className="py-20 bg-muted/20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-3xl lg:text-4xl font-heading font-bold tracking-tight text-foreground mb-4">
              {content?.features_title}
            </h3>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {content?.features_subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {content?.features?.map((feature, index) => {
              const Icon = getIcon(feature.icon);
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-card p-6 rounded-lg border border-border hover:border-primary/50 transition-colors"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h4 className="text-xl font-heading font-semibold text-foreground mb-2">
                    {feature.title}
                  </h4>
                  <p className="text-muted-foreground">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-primary text-primary-foreground rounded-2xl p-12 text-center"
          >
            <h3 className="text-3xl lg:text-4xl font-heading font-bold mb-4">
              {content?.cta_title}
            </h3>
            <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
              {content?.cta_subtitle}
            </p>
            <Button
              size="lg"
              variant="secondary"
              onClick={() => {
                trackCtaClick('hero_primary');
                navigate(hero?.primary_cta?.url || '/apply');
              }}
              className="font-semibold"
              data-testid="cta-apply-button"
            >
              {content?.cta_button_text}
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8 bg-card">
        <div className="container mx-auto px-6 text-center text-muted-foreground">
          {branding?.footer_html ? (
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(branding.footer_html) }} />
          ) : (
            <p>&copy; {new Date().getFullYear()} {appName}. All rights reserved.</p>
          )}
        </div>
      </footer>
    </div>
  );
};

export default Landing;
